import React, { useState, useEffect } from 'react';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import StatCard from '../components/dashboard/StatCard';
import ActiveJobs from '../components/dashboard/ActiveJobs';
import CompletedJobs from '../components/dashboard/CompletedJobs';
import NewJobButton from '../components/dashboard/NewJobButton';
import NewJobModal from '../components/dashboard/NewJobModal';
import KeyInsights from '../components/dashboard/KeyInsights';
import AddCandidateButton from '../components/candidates/AddCandidateButton';
import AddCandidateModal from '../components/candidates/AddCandidateModal';
import { FileText, Clock, AlertCircle, User, Loader2, Star } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { candidatesApi } from '../api/candidates';
import { jobsApi } from '../api/jobs';

interface DashboardStats {
  totalReferences: number;
  pendingReferences: number;
  highPriorityJobs: number;
  totalJobs: number;
  pendingCandidates: number;
  totalCandidates: number;
}

const Dashboard = () => {
  const [showNewJobModal, setShowNewJobModal] = useState(false);
  const [showAddCandidateModal, setShowAddCandidateModal] = useState(false);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState<import('../data/mockJobs').Job[]>([]);
  const { user, loading: authLoading } = useAuth();
  const { t } = useLanguage();

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      
      // Fetch all candidates and jobs
      const [candidates, jobsData] = await Promise.all([
        candidatesApi.getAll(),
        jobsApi.getAll()
      ]);
      
      // Transform API job data to match expected format in components, filter out cancelled jobs
      const transformedJobs = jobsData
        .filter(job => job.status !== 'CANCELLED')
        .map(job => ({
          id: job.id,
          title: job.job_title,
          department: job.department,
          location: 'Remote', // Default location
          candidates: 0, // Default value
          referencesNeeded: job.required_number_of_references,
          referencesCompleted: 0, // Default value
          status: job.status || 'ACTIVE', // Preserve original status
          priority: 'medium', // Default value
          dueDate: job.application_deadline || new Date().toISOString().split('T')[0]
        }));
      
      setJobs(transformedJobs);

      // Get active jobs
      const activeJobs = jobsData.filter(job => job.status !== 'COMPLETED' && job.status !== 'CANCELLED');
      const activeJobIds = new Set(activeJobs.map(job => job.id));
      
      // Get candidates for active jobs only
      const activeJobCandidates = candidates.filter(candidate => 
        candidate.jobId && activeJobIds.has(candidate.jobId)
      );
      
      // Calculate total references and completed references for active jobs only
      let totalReferences = 0;
      let completedReferences = 0;

      // Process each candidate's references for active jobs only
      activeJobCandidates.forEach(candidate => {
        if (candidate.references) {
          totalReferences += candidate.references.length;
          completedReferences += candidate.references.filter(ref => ref.filled_form_at).length;
        }
      });

      // Calculate stats
      const pendingReferences = totalReferences - completedReferences;

      // Count high priority jobs (due within less than 2 days) from active jobs only
      const highPriorityJobs = jobsData.reduce((count, job) => {
        // Skip completed or cancelled jobs
        if (job.status === 'COMPLETED' || job.status === 'CANCELLED') return count;
        
        const dueDate = new Date(job.application_deadline);
        const today = new Date();
        const daysUntilDue = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        return count + (daysUntilDue < 2 ? 1 : 0);
      }, 0);
      
      // Count pending candidates (candidates who haven't filled their forms yet or have empty references) for active jobs only
      const pendingCandidates = activeJobCandidates.reduce((count, candidate) => {
        // A candidate is pending if:
        // 1. They haven't filled their form yet, OR
        // 2. They have no references at all
        const hasEmptyReferences = !candidate.references || candidate.references.length === 0;
        return count + (!candidate.form_filled_at || hasEmptyReferences ? 1 : 0);
      }, 0);

      // Filter out cancelled jobs for total count
      const activeAndCompletedJobs = jobsData.filter(job => job.status !== 'CANCELLED');
      
      setStats({
        totalReferences,
        pendingReferences,
        highPriorityJobs,
        totalJobs: activeJobs.length,
        pendingCandidates,
        totalCandidates: activeJobCandidates.length
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      
      <div className="flex-1 flex flex-col">
        <Header />
        
        <main className="flex-1 p-6 space-y-8">
          {/* Header Section */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {authLoading ? (
                  <div className="flex items-center">
                    <Loader2 className="w-5 h-5 mr-2 animate-spin text-blue-600" />
                    {t('common.loading')}
                  </div>
                ) : (
                  <>{t('dashboard.title')}</>
                )}
              </h1>
              <p className="text-gray-500 text-sm tracking-wide">{t('dashboard.subtitle')}</p>
            </div>
            <div className="flex items-center gap-3">
              <NewJobButton onClick={() => setShowNewJobModal(true)} />
              <AddCandidateButton onClick={() => setShowAddCandidateModal(true)} />
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mx-auto w-full">
            {loading ? (
              Array(4).fill(null).map((_, i) => (
                <div key={i} className="bg-white rounded-lg border p-4 animate-pulse min-h-[100px]">
                  <div className="h-5 w-24 bg-gray-200 rounded mb-4"></div>
                  <div className="h-8 w-16 bg-gray-200 rounded"></div>
                </div>
              ))
            ) : stats && (
              <>
                <StatCard
                  icon={FileText}
                  title={t('dashboard.stats.totalReferences')}
                  value={stats.totalReferences}
                  progress={100}
                  color="bg-blue-500"
                  showProgress={stats.totalReferences > 0}
                />
                <StatCard
                  icon={AlertCircle}
                  title={t('dashboard.stats.pendingCandidates')}
                  value={stats.pendingCandidates}
                  progress={Math.round((stats.pendingCandidates / (stats.totalCandidates || 1)) * 100)}
                  color="bg-red-500"
                  showProgress={stats.pendingCandidates > 0}
                />
                <StatCard
                  icon={Clock}
                  title={t('dashboard.stats.pendingReferences')}
                  value={stats.pendingReferences}
                  progress={Math.round((stats.pendingReferences / (stats.totalReferences || 1)) * 100)}
                  color="bg-orange-500"
                  showProgress={stats.pendingReferences > 0}
                />
                <StatCard
                  icon={Star}
                  title={t('dashboard.stats.completedReferences')}
                  value={stats.totalReferences - stats.pendingReferences}
                  progress={Math.round(((stats.totalReferences - stats.pendingReferences) / (stats.totalReferences || 1)) * 100)}
                  color="bg-green-500"
                  showProgress={(stats.totalReferences - stats.pendingReferences) > 0}
                />
              </>
            )}
          </div>

          {/* Key Insights Section */}
          <div className="bg-gradient-to-br from-gray-50 to-white p-0.5 rounded-lg">
            <KeyInsights />
          </div>

          {/* Active Jobs Section */}
          <div className="bg-gradient-to-br from-blue-50 to-white p-0.5 rounded-lg shadow-md">
            <div className="bg-white rounded-lg border-l-4 border-blue-500">
              <ActiveJobs />
            </div>
          </div>

          {/* Section Divider */}
          <div className="relative my-12">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
          </div>

          {/* Completed Jobs Section */}
          <div className="bg-gradient-to-br from-purple-50 to-white p-0.5 rounded-lg shadow-md">
            <div className="bg-white rounded-lg border-l-4 border-purple-500">
              <CompletedJobs />
            </div>
          </div>

          {showNewJobModal && (
            <NewJobModal onClose={() => setShowNewJobModal(false)} />
          )}
          
          {showAddCandidateModal && (
            <AddCandidateModal
              jobs={jobs.filter(job => job.status !== 'COMPLETED' && job.status !== 'CANCELLED')}
              onCandidateCreated={(newCandidate) => {
                // Refresh dashboard stats after adding a new candidate
                fetchDashboardStats();
              }}
              onClose={() => setShowAddCandidateModal(false)}
            />
          )}
        </main>
      </div>
    </div>
  );
};

export default Dashboard;